import { useState, useContext, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaTshirt, FaShoppingCart, FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaFileAlt, FaPaperPlane, FaSearch, FaFilter, FaTag } from 'react-icons/fa';
import Emailjs from '../services/Emailjs';
import { DContext } from '../store/DataContext';
import productsData from '../data/products.json';

// Dynamically load all product images based on JSON filenames
// Webpack (react-scripts) supports require.context for bundling these assets.
const productImages = require.context('../assets/Products', false, /\.(png|jpe?g|webp|svg)$/);

function getImageSrc(filename) {
    if (!filename) return '';
    try {
        return productImages(`./${filename}`);
    } catch (e) {
        return '';
    }
}

function Products() {
    const { showAlert } = useContext(DContext);
    const [enquiryOpen, setEnquiryOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        contact: '',
        email: '',
        city: '',
        subject: '',
        description: ''
    });
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const validCategories = ['men', 'women', 'kids'];
    const initialCategory = searchParams.get('category');
    const [categoryFilter, setCategoryFilter] = useState(validCategories.includes(initialCategory) ? initialCategory : ''); // '', 'men', 'women', 'kids'

    // Sync category state when URL param changes manually
    useEffect(() => {
        const paramCategory = searchParams.get('category');
        if (paramCategory && validCategories.includes(paramCategory) && paramCategory !== categoryFilter) {
            setCategoryFilter(paramCategory);
        } else if (!paramCategory && categoryFilter !== '') {
            setCategoryFilter('');
        }
    }, [searchParams]);

    const filteredProducts = useMemo(() => {
        return productsData.filter(p => {
            const matchCategory = categoryFilter ? p.category === categoryFilter : true;
            const term = search.trim().toLowerCase();
            const matchSearch = term
                ? p.title.toLowerCase().includes(term) ||
                p.sku.toLowerCase().includes(term) ||
                p.tags.some(t => t.toLowerCase().includes(term))
                : true;
            return matchCategory && matchSearch;
        });
    }, [productsData, search, categoryFilter]);

    const openEnquiry = (product) => {
        setSelectedProduct(product);
        setEnquiryOpen(true);
    };
    const closeEnquiry = () => {
        setEnquiryOpen(false);
        setSelectedProduct(null);
        setFormData({ name: '', contact: '', email: '', city: '', subject: '', description: '' });
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name required';
        else if (formData.name.trim().length < 2) newErrors.name = 'Min 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(formData.name)) newErrors.name = 'Letters & spaces only';

        if (!formData.contact.trim()) newErrors.contact = 'Contact required';
        else if (!/^[6-9]\d{9}$/.test(formData.contact)) newErrors.contact = 'Invalid mobile';

        if (formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) newErrors.email = 'Invalid email';
        }

        if (!formData.city.trim()) newErrors.city = 'City required';
        else if (formData.city.trim().length < 2) newErrors.city = 'Min 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(formData.city)) newErrors.city = 'Letters & spaces only';

        if (!formData.subject.trim()) newErrors.subject = 'Subject required';
        else if (formData.subject.trim().length < 3) newErrors.subject = 'Min 3 characters';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            showAlert('Fix form errors before submitting');
            return;
        }
        setIsSubmitting(true);
        try {
            await Emailjs({
                from_name: formData.name,
                contact: formData.contact,
                email: formData.email || 'Not provided',
                city: formData.city,
                product: selectedProduct?.title || 'Unknown',
                subject: formData.subject,
                message: formData.description || 'No additional details'
            });
            await showAlert('Enquiry submitted successfully');
            closeEnquiry();
        } catch (err) {
            console.log("Enquiry submission error:", err);
            showAlert('Failed to submit enquiry');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Hero */}
            <section className="bg-primary-600 text-white py-16 px-4 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-700" />
                </div>
                <div className="max-w-6xl mx-auto relative z-10 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">Products</h1>
                    <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto">Browse our curated garment catalog. Filter by category or search by name, SKU, or tag.</p>
                </div>
            </section>

            {/* Filters */}
            <div className="max-w-7xl mx-auto px-4 mt-10">
                <div className="bg-white rounded-2xl shadow-md p-5 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-2 text-gray-700 font-semibold"><FaFilter className="text-primary-600" /> Filters</div>
                    <div className="flex-1 relative">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search products by title, SKU, or tag..."
                            className="w-full pl-12 pr-4 py-3 rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {['', 'men', 'women', 'kids'].map(cat => (
                            <button
                                key={cat || 'all'}
                                onClick={() => {
                                    setCategoryFilter(cat);
                                    if (cat) {
                                        setSearchParams({ category: cat });
                                    } else {
                                        setSearchParams({});
                                    }
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${categoryFilter === cat ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                    }`}
                            >
                                {cat === '' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Products Grid */}
            <div className="max-w-7xl mx-auto px-4 py-12">
                {filteredProducts.length === 0 && (
                    <div className="text-center py-20 text-gray-500">No products match your filters.</div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {filteredProducts.map(p => {
                        const discount = p.offerPrice && p.offerPrice < p.actualPrice ? Math.round(((p.actualPrice - p.offerPrice) / p.actualPrice) * 100) : 0;
                        return (
                            <div key={p.productId} className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-500 overflow-hidden flex flex-col">
                                <Link to={`/product/${p.productId}`} className="relative block overflow-hidden">
                                    <img src={getImageSrc(p.images[0])} alt={p.title} className="w-full h-44 object-cover transform group-hover:scale-105 transition duration-500" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition" />
                                    {discount > 0 && (<span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded">-{discount}%</span>)}
                                    {p.badge && (<span className="absolute top-3 right-3 bg-primary-600 text-white text-xs font-semibold px-2 py-1 rounded">{p.badge}</span>)}
                                </Link>
                                <div className="p-5 flex flex-col flex-1">
                                    <h3 className="text-base font-bold text-gray-800 flex items-center gap-2 mb-1"><FaTshirt className="text-primary-600" /> {p.title}</h3>
                                    <div className="text-xs text-gray-500 mb-2">SKU: {p.sku}</div>
                                    <p className="text-sm text-gray-600 mb-3 line-clamp-3">{p.desc || 'High quality garment from SS Tex.'}</p>
                                    <div className="mb-3 flex items-center gap-2">
                                        {p.offerPrice && p.offerPrice < p.actualPrice ? (
                                            <>
                                                <span className="text-lg font-bold text-primary-600">₹{p.offerPrice}</span>
                                                <span className="text-sm line-through text-gray-400">₹{p.actualPrice}</span>
                                            </>
                                        ) : (
                                            <span className="text-lg font-bold text-gray-800">₹{p.actualPrice}</span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {p.tags.slice(0, 3).map(t => (
                                            <span key={t} className="text-xs flex items-center gap-1 bg-primary-50 text-primary-600 px-2 py-1 rounded-full border border-primary-100"><FaTag /> {t}</span>
                                        ))}
                                    </div>
                                    <div className="mt-auto flex gap-3">
                                        <Link to={`/product/${p.productId}`} className="flex-1 inline-flex items-center justify-center text-sm font-semibold bg-white border border-primary-600 text-primary-600 rounded-lg py-2 hover:bg-primary-50 transition">Details</Link>
                                        <button onClick={() => openEnquiry(p)} className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold bg-primary-600 text-white rounded-lg py-2 hover:bg-primary-700 transition">
                                            <FaShoppingCart /> Enquire
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Enquiry Modal */}
            {enquiryOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={closeEnquiry} />
                    <div className="relative bg-white w-full max-w-xl mx-auto rounded-2xl shadow-2xl p-8 mt-10">
                        <h2 className="text-2xl font-bold mb-1">Enquiry - {selectedProduct?.title}</h2>
                        <p className="text-xs text-gray-500 mb-4">SKU: {selectedProduct?.sku} | Category: {selectedProduct?.category}</p>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                                <div className="relative">
                                    <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="name" value={formData.name} onChange={handleChange} type="text" placeholder="Your full name" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                            </div>
                            {/* Contact */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Contact *</label>
                                <div className="relative">
                                    <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="contact" value={formData.contact} onChange={handleChange} type="tel" maxLength={10} placeholder="10-digit mobile" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.contact ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.contact && <p className="text-xs text-red-500 mt-1">{errors.contact}</p>}
                            </div>
                            {/* Email (optional) */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Email (Optional)</label>
                                <div className="relative">
                                    <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="email" value={formData.email} onChange={handleChange} type="email" placeholder="you@example.com" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                            </div>
                            {/* City */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">City *</label>
                                <div className="relative">
                                    <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="city" value={formData.city} onChange={handleChange} type="text" placeholder="Your city" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.city ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
                            </div>
                            {/* Subject */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Subject *</label>
                                <div className="relative">
                                    <FaFileAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="subject" value={formData.subject} onChange={handleChange} type="text" placeholder="Short subject" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.subject ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
                            </div>
                            {/* Description (optional) */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Description (Optional)</label>
                                <textarea name="description" value={formData.description} onChange={handleChange} rows={3} placeholder="Additional details..." className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={closeEnquiry} className="flex-1 py-3 rounded-lg border border-gray-300 font-semibold text-gray-700 hover:bg-gray-100 transition">Cancel</button>
                                <button type="submit" disabled={isSubmitting} className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 text-white bg-primary-600 hover:bg-primary-700 transition ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>{isSubmitting ? (<><div className="animate-spin h-5 w-5 border-b-2 border-white rounded-full" /><span>Sending...</span></>) : (<><FaPaperPlane /><span>Send</span></>)}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Products;